<template>
    <div>
        <div class="flex justify-end items-center gap-2">
            <span>Sortiere nach Preis: </span>
            <USelect
                v-model="sortType"
                :items="sortOptions"
                class="w-48"
            />
        </div>

        <section
            v-if="articles?.length>0"
            class="grid gap-x-8 gap-y-8 py-8 md:grid-cols-2 md:gap-y-16 md:py-16 2xl:grid-cols-3"
        >
            <NuxtLink
                v-for="(article, index) in articles"
                :key="index"
                class="panel"
                role="menuitem"
                tabindex="-1"
                :to="{
                    name:'product',
                    params: {
                        id: article._id,
                        name: StringTools.simplify(article.name + ' ' + article.name2),
                        category: StringTools.simplify($route.params?.name ?? article.categories[0]?.name ?? ''),
                    }
                }"
            >
              <div class="relative">
                <NuxtPicture
                    v-if="article.previewImage"
                    provider="apiImage"
                    :alt="article.previewImage.alternativeText ?? article.name"
                    :img-attrs="{ class: 'w-full h-full object-cover rounded-t-lg' }"
                    :src="`${article.previewImage._id}/${encodeURIComponent(article.previewImage.name)}`"
                    format="webp"
                    height="360"
                    sizes="100vw md:50vw 2xl:33vw"
                    width="640"
                />
                <NuxtPicture
                    v-else
                    src="articles/placeholder.jpg"
                    :alt="article.name"
                    :img-attrs="{ class: 'w-full h-full object-cover rounded-t-lg' }"
                    height="360"
                    width="640"
                />
                <img
                    v-if="getAiLabelSrc(article.previewImage)"
                    :src="getAiLabelSrc(article.previewImage)!"
                    alt="AI Label"
                    class="absolute bottom-2 right-2 h-8 pointer-events-none select-none"
                >
              </div>
                <div class="flex flex-col gap-2 items-center px-6 w-full xl:flex-row">
                    <div class="basis-1/2 place-self-start">
                        <p class="text-base text-secondary! font-bold lg:text-lg!">
                            {{ article.name }}
                        </p>
                        <p class="text-neutral-500!">
                            {{ article.articleNumber }}
                        </p>
                    </div>
                    <div class="basis-1/2 space-y-4 place-self-start text-right">
                        <div class="flex flex-col font-bold text-right text-secondary">
                            <template v-if="article.totalPrice >= 25000">
                                <span>Preis auf Anfrage</span>
                            </template>
                            <template v-else-if="article.totalPrice > 0">
                                <template v-if="StringTools.showPrice(article)">
                                    <span>ab {{
                                            StringTools.currencyString(article.totalPrice)
                                        }}{{ article.unit ? ' / ' + article.unit : '' }}</span>
                                    <span class="text-xs font-normal text-neutral-500">netto ab Werk</span>
                                </template>
                                <template v-else>
                                    <span>&nbsp;</span>
                                </template>
                            </template>
                        </div>
                        <UButton
                            color="secondary"
                            label="Zum Produkt"
                            leading-icon="lucide:arrow-right"
                            variant="outline"
                        />
                    </div>
                </div>
                <div class="icon-3d">
                    <UTooltip
                        class="overflow-hidden"
                        text="AR-Modus öffnen"
                    >
                        <div
                            class="icon-ar relative"
                            @click.stop.prevent="showARModal = true"
                        >
                            <div class="hexagon">
                                <Icon3D
                                    id="Icon3D"
                                    :tw-class="''"
                                />
                            </div>
                            <IconPhone
                                id="IconPhone"
                                :tw-class="''"
                            />
                        </div>
                    </UTooltip>

                    <UTooltip :text="article.onWishlist ? 'Vom Merkzettel entfernen' : 'Auf den Merkzettel'">
                        <div
                            class="relative z-20 flex! items-center justify-center p-0! shadow-none"
                            @click.stop.prevent="toggleWishlist(article)"
                        >
                            <UButton
                                :icon="article.onWishlist ? 'i-material-symbols:kid-star' : 'i-material-symbols:kid-star-outline'"
                                class="bg-neutral-50 text-secondary hover:bg-neutral-100 size-12 justify-center rounded-lg"
                            />
                        </div>
                    </UTooltip>
                </div>

                <UModal v-model:open="showARModal">
                    <template #body>
                        <ARModal :article="article"/>
                    </template>
                </UModal>
            </NuxtLink>
        </section>

        <section
            v-else
            class="py-16 space-y-8"
        >
            <h2 class="text-2xl text-center text-secondary lg:text-5xl">
                Kein Ergebnis gefunden!
            </h2>
            <p class="text-center">
                Zu den gewählten Filteroptionen wurde leider kein Ergebnis gefunden!
            </p>
        </section>
    </div>
</template>
<script setup lang="ts">
import {onMounted} from 'vue';
import {StringTools} from '~~/utils/StringTools';
import {getAiLabelSrc} from '~~/utils/ai-image-label';
import IconPhone from '~/components/Icons/IconPhone.vue';
import Icon3D from '~/components/Icons/Icon3D.vue';
import ARModal from '~/components/Modal/ARModal.vue';
import {useWishlistStore} from '~~/stores/wishlist';
import {useCombinationModelStore} from '~~/stores/combinationmodel';
import {useFilterStore} from '~~/stores/filter';

const route = useRoute();
const router = useRouter();

const props = defineProps<{
    articles?: any,
    categoryId?: any,
    isSearching?: boolean
}>();

let filters = {};
const showARModal = ref(false);
const combinationModelStore = useCombinationModelStore();
const wishlistStore = useWishlistStore();
await wishlistStore.init();

const sortType = ref<'asc' | 'desc' | null>(null);
const sortOptions = ref([
    {
        label: 'Niedrigster zuerst',
        value: 'asc',
    },
    {
        label: 'Höchster zuerst',
        value: 'desc',
    },
    {
        label: 'Standard',
        value: null,
    },
]);
const emit = defineEmits<{(e: 'sort', order: 'asc' | 'desc' | null): void
}>();
watch(sortType, async (opt) => {
  filters.order = opt;

  const nextQuery = {...route.query};
  if (opt === null) {
    delete nextQuery.order;
  } else {
    nextQuery.order = opt;
  }

  await router.replace({query: nextQuery});
  emit('sort', opt);
});

watch(
    () => props.articles,
    (articles) => {
        articles?.forEach((article: any) => {
            article.onWishlist = wishlistStore.has(article);
        });
    },
    {
        immediate: true,
        deep: true,
    },
);

async function toggleWishlist(article: any) {
    if (article.onWishlist) {
        await wishlistStore.remove(article);
        article.onWishlist = false;
    } else {
        await wishlistStore.add(article);
        article.onWishlist = true;
    }
    // $forceUpdate();
}

onMounted(async () => {
    const filterStore = useFilterStore();
    filters = filterStore.filters;
});
</script>

<style scoped>

</style>
